import {
  Flex,
  FlexProps,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../../../../components/Pagination';
import useDebounce from '../../../../hooks/useDebounce';
import { CoupomAPI } from '../../api';
import { CreateListContext } from '../../context/ListContext';
import { Coupom } from '../../models/coupom';
import EditCoupomModal from '../EditModal';
import ItemList from './ItemList';

const CoupomList: FC<FlexProps> = ({ ...rest }) => {
  const { debounce } = useDebounce();
  const [UpdateListCtx, setUpdateListFn] = useContext(CreateListContext);
  const [items, setItems] = useState<Coupom[]>([]);
  const [selectedItem, setSelectedItem] = useState<Coupom>();
  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();
  const findItems = (page?: number, limit?: number) => {
    setSelectedItem(undefined);
    setItems([]);
    const coupomAPI = new CoupomAPI();
    return coupomAPI
      .findAll({
        limit: limit || UpdateListCtx.paginationInfo.limit,
        page: page || UpdateListCtx.paginationInfo.page,
        search,
      })
      .then(res => {
        setItems(res.data);
        setUpdateListFn(prev => ({
          ...prev,
          paginationInfo: {
            ...prev.paginationInfo,
            totalPages: res.totalPages,
            total: res.total,
          },
        }));
      });
  };

  useEffect(() => {
    const { page, limit } = UpdateListCtx.paginationInfo;
    findItems(page, limit);
    setUpdateListFn(prev => ({ ...prev, updateListFn: findItems }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { page, limit } = UpdateListCtx.paginationInfo;
    setIsSearching(true);
    debounce(
      () => findItems(page, limit).finally(() => setIsSearching(false)),
      500,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleOpenEditModal = (data: Coupom) => {
    setSelectedItem(data);
    onOpen();
  };

  return (
    <>
      <EditCoupomModal isOpen={isOpen} onClose={onClose} data={selectedItem} />
      <Flex alignItems="center" gridGap="2" mt="10">
        <Text as="label" size="sm">
          {`${t('search')}:`}
        </Text>
        <Input
          size="sm"
          rounded="base"
          placeholder={t('enterCouponCodeOrName')}
          maxW="256px"
          defaultValue={search}
          onChange={e => setSearch(e.target.value)}
        />
        {isSearching ? (
          <Spinner
            thickness="2px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="sm"
          />
        ) : undefined}
      </Flex>
      <Flex wrap="wrap" gridGap="3" mt="5" {...rest}>
        {items.length ? (
          items.map(item => (
            <ItemList
              key={item.id}
              data={item}
              onOpenModal={handleOpenEditModal}
            />
          ))
        ) : (
          <Text textAlign="center" w="full" opacity="0.7">
            {t('couponsNotFound')}
          </Text>
        )}
      </Flex>
      <Flex justifyContent="center" mt="5">
        <Pagination
          current={UpdateListCtx.paginationInfo.page}
          perPage={UpdateListCtx.paginationInfo.limit}
          total={UpdateListCtx.paginationInfo.total}
          onPageChange={page => {
            if (page > UpdateListCtx.paginationInfo.totalPages) return;
            setUpdateListFn(prev => ({
              ...prev,
              paginationInfo: { ...prev.paginationInfo, page },
            }));
            findItems(page, UpdateListCtx.paginationInfo.limit);
          }}
        />
      </Flex>
    </>
  );
};

export default CoupomList;
